// src/utils/planGeometry.js
//
// Polygon helpers for the plan editor. All of it works in image pixel space;
// metres arrive only by multiplying with the confirmed scale.
//
// The wall-gap rule needs a real polygon-to-polygon distance, not a
// bounding-box test: two L-shaped rooms can have overlapping boxes while their
// actual walls sit a metre apart, and a box test would refuse a legal edit.

export function polyArea(pts) {
  let a = 0;
  for (let i = 0, n = pts.length; i < n; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % n];
    a += x1 * y2 - x2 * y1;
  }
  return Math.abs(a) / 2;
}

export function bbox(pts) {
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

function pointSegDist(p, a, b) {
  const vx = b[0] - a[0];
  const vy = b[1] - a[1];
  const len2 = vx * vx + vy * vy;
  let t = len2 ? ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const dx = a[0] + t * vx - p[0];
  const dy = a[1] + t * vy - p[1];
  return Math.hypot(dx, dy);
}

function segIntersect(a1, a2, b1, b2) {
  const d = (p, q, r) =>
    Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]));
  const d1 = d(a1, a2, b1);
  const d2 = d(a1, a2, b2);
  const d3 = d(b1, b2, a1);
  const d4 = d(b1, b2, a2);
  return d1 !== d2 && d3 !== d4;
}

function segSegDist(a1, a2, b1, b2) {
  if (segIntersect(a1, a2, b1, b2)) return 0;
  return Math.min(
    pointSegDist(a1, b1, b2),
    pointSegDist(a2, b1, b2),
    pointSegDist(b1, a1, a2),
    pointSegDist(b2, a1, a2)
  );
}

export function pointInPoly(p, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if ((yi > p[1]) !== (yj > p[1]) &&
        p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/** True when two simple polygons share any area. */
export function polysOverlap(A, B) {
  for (let i = 0; i < A.length; i++) {
    const a1 = A[i];
    const a2 = A[(i + 1) % A.length];
    for (let j = 0; j < B.length; j++) {
      if (segIntersect(a1, a2, B[j], B[(j + 1) % B.length])) return true;
    }
  }
  // no crossing edges: one may still be fully inside the other
  return pointInPoly(A[0], B) || pointInPoly(B[0], A);
}

/** Smallest gap between two polygons, 0 if they touch or overlap. */
export function polyGap(A, B) {
  if (polysOverlap(A, B)) return 0;
  let best = Infinity;
  for (let i = 0; i < A.length; i++) {
    const a1 = A[i];
    const a2 = A[(i + 1) % A.length];
    for (let j = 0; j < B.length; j++) {
      const d = segSegDist(a1, a2, B[j], B[(j + 1) % B.length]);
      if (d < best) best = d;
    }
  }
  return best;
}

/**
 * Clip a polygon to one side of an axis-aligned line (Sutherland–Hodgman).
 * `axis` is 'x' or 'y'; `keep` is 'lt' or 'gt'.
 * Exact for the rectangles rooms start as, which is what splitting is for.
 */
export function clipHalfPlane(pts, axis, at, keep) {
  const k = axis === 'x' ? 0 : 1;
  const inside = (p) => (keep === 'lt' ? p[k] <= at : p[k] >= at);
  const out = [];
  for (let i = 0; i < pts.length; i++) {
    const cur = pts[i];
    const prev = pts[(i - 1 + pts.length) % pts.length];
    const ci = inside(cur);
    const pi = inside(prev);
    if (ci !== pi) {
      const t = (at - prev[k]) / (cur[k] - prev[k]);
      const ip = [prev[0] + t * (cur[0] - prev[0]), prev[1] + t * (cur[1] - prev[1])];
      ip[k] = at;
      out.push(ip);
    }
    if (ci) out.push(cur);
  }
  return out;
}

/**
 * Number rooms in reading order — banded top-to-bottom, then left-to-right.
 *
 * Banding matters: rooms in one row of a plan are never at identical y, so a
 * plain sort by y interleaves the row with the one below it and the numbers
 * zig-zag across the drawing. Rounding y to a band first keeps a row together,
 * so the badge on the plan and the row in the table are found in the same sweep
 * of the eye.
 *
 * Items need a `pts` polygon. Returns them with `n`, `box` and `centroid` added.
 */
export function readingOrder(items, sheetH) {
  const band = Math.max(1, sheetH * 0.06);
  return items
    .map((it) => {
      const b = bbox(it.pts);
      return {
        ...it,
        box: b,
        centroid: [(b.x0 + b.x1) / 2, (b.y0 + b.y1) / 2],
      };
    })
    .sort((a, b) => {
      // Panel first, so the numbers run through one floor plan before starting
      // the next — a schedule sectioned by floor with numbers jumping between
      // sections would be unusable. Regions on no panel sort last.
      const pa = a.panel == null ? 1e6 : a.panel;
      const pb = b.panel == null ? 1e6 : b.panel;
      if (pa !== pb) return pa - pb;
      const ra = Math.round(a.centroid[1] / band);
      const rb = Math.round(b.centroid[1] / band);
      return ra !== rb ? ra - rb : a.centroid[0] - b.centroid[0];
    })
    .map((it, i) => ({ ...it, n: i + 1 }));
}

/** Drop vertices that sit on the straight line between their neighbours. */
export function dedupe(pts, eps = 0.6) {
  const out = [];
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    const q = out[out.length - 1];
    if (q && Math.hypot(p[0] - q[0], p[1] - q[1]) < eps) continue;
    out.push(p);
  }
  if (out.length > 2) {
    const f = out[0];
    const l = out[out.length - 1];
    if (Math.hypot(f[0] - l[0], f[1] - l[1]) < eps) out.pop();
  }
  return out;
}
